import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import * as XLSX from "xlsx";

// ── Design tokens — Minimal + Sunshine + Fraunces/Jakarta ──
const C = {
    bg: "#ffffff",
    surface: "#f7f7f7",
    border: "#e0e0e0",
    text: "#000000",
    muted: "#555555",
    dim: "#888888",
    accent: "#52658F",
    cyan: "#2a6a8a",
    peach: "#ECDBC9",
    grey: "#CDCDCD",
    green: "#059669",
    navy: "#000000",
};
const SYNE = "'Fraunces', serif";
const MONO = "'Plus Jakarta Sans', sans-serif";
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";

// ── RFM Engine (Quartile / Default) ─────────────────────────
function parseDate(str) {
    if (!str) return null;
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) return new Date(str);
    const ddmmyyyy = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (ddmmyyyy) return new Date(`${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`);
    const ddmmyyyy2 = str.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (ddmmyyyy2) return new Date(`${ddmmyyyy2[3]}-${ddmmyyyy2[2]}-${ddmmyyyy2[1]}`);
    return new Date(str);
}

function percentileScore(value, allValues, reverse = false) {
    const sorted = [...allValues].sort((a, b) => a - b);
    const rank = sorted.findIndex(v => v >= value) / sorted.length;
    const pct = reverse ? 1 - rank : rank;
    if (pct >= 0.8) return 5;
    if (pct >= 0.6) return 4;
    if (pct >= 0.4) return 3;
    if (pct >= 0.2) return 2;
    return 1;
}

function computeQuartileCutoffs(allValues, reverse = false) {
    const sorted = [...allValues].sort((a, b) => a - b);
    const n = sorted.length;
    const q = (p) => sorted[Math.floor(p * n)] ?? sorted[n - 1];
    return { p20: q(0.2), p40: q(0.4), p60: q(0.6), p80: q(0.8) };
}

function classifySegment(r, f, m) {
    if (r >= 4 && f >= 4) return { segment: "Champions", label: "Champions 🏆", color: "#059669", bg: "#d1fae5", description: "Your best customers. They buy often, recently, and spend the most.", action: "Reward them. Ask for reviews. Offer early access to new products." };
    if (r >= 3 && f >= 3 && m >= 3) return { segment: "Loyal", label: "Loyal Customers ⭐", color: "#52658F", bg: "#e8ecf4", description: "Consistent customers who order regularly and spend well.", action: "Upsell premium items. Invite to loyalty program. Send thank you messages." };
    if (r >= 4 && f <= 2) return { segment: "New", label: "New Customers 🌱", color: "#2a6a8a", bg: "#dbeafe", description: "Ordered recently but haven't come back enough yet.", action: "Send a welcome promo. Encourage second order with a discount." };
    if (r >= 3 && f >= 2 && m >= 2) return { segment: "Promising", label: "Promising 📈", color: "#b45309", bg: "#fef3c7", description: "Showing good signs — recent orders and growing frequency.", action: "Nurture with targeted offers. Show them your best-selling items." };
    if (r <= 2 && f >= 3) return { segment: "At Risk", label: "At Risk ⚠️", color: "#c2410c", bg: "#ffedd5", description: "Used to order frequently but haven't ordered recently.", action: "Send a win-back campaign. Offer a special comeback discount." };
    if (r <= 2 && f <= 2 && m >= 3) return { segment: "Can't Lose", label: "Can't Lose Them 💎", color: "#dc2626", bg: "#fee2e2", description: "High spenders who haven't ordered in a while. Critical to recover.", action: "Personal outreach. Significant discount or free item. Act fast." };
    if (r <= 1 && f <= 1) return { segment: "Lost", label: "Lost Customers 💤", color: "#888888", bg: "#f3f4f6", description: "Haven't ordered in a long time and ordered infrequently.", action: "Low priority. Try a re-engagement campaign if budget allows." };
    return { segment: "Needs Attention", label: "Needs Attention 🔍", color: "#777777", bg: "#f3f4f6", description: "Average across all dimensions — worth monitoring.", action: "Encourage more frequent ordering with a small incentive." };
}

function runRFM(rows, customerCol, dateCol, amountCol) {
    const customerMap = {};
    const today = new Date();
    rows.forEach(row => {
        const customerId = row[customerCol];
        const date = parseDate(row[dateCol]);
        const amount = parseFloat(row[amountCol]);
        if (!customerId || !date || isNaN(amount)) return;
        if (!customerMap[customerId]) customerMap[customerId] = { customerId, orders: [], totalSpend: 0 };
        customerMap[customerId].orders.push({ date, amount });
        customerMap[customerId].totalSpend += amount;
    });
    const customers = Object.values(customerMap);
    if (customers.length === 0) return { results: [], cutoffs: null };
    const rfmRaw = customers.map(c => {
        const lastOrder = new Date(Math.max(...c.orders.map(o => o.date)));
        const recencyDays = Math.floor((today - lastOrder) / (1000 * 60 * 60 * 24));
        return { customerId: c.customerId, recencyDays, frequency: c.orders.length, monetary: c.totalSpend, lastOrderDate: lastOrder.toISOString().split("T")[0] };
    });
    const allRecency = rfmRaw.map(r => r.recencyDays);
    const allFrequency = rfmRaw.map(r => r.frequency);
    const allMonetary = rfmRaw.map(r => r.monetary);
    const cutoffs = {
        recency: computeQuartileCutoffs(allRecency, true),
        frequency: computeQuartileCutoffs(allFrequency, false),
        monetary: computeQuartileCutoffs(allMonetary, false),
    };
    const results = rfmRaw.map(r => {
        const rScore = percentileScore(r.recencyDays, allRecency, true);
        const fScore = percentileScore(r.frequency, allFrequency, false);
        const mScore = percentileScore(r.monetary, allMonetary, false);
        const segment = classifySegment(rScore, fScore, mScore);
        return { ...r, rScore, fScore, mScore, rfmScore: ((rScore + fScore + mScore) / 3).toFixed(1), ...segment };
    }).sort((a, b) => parseFloat(b.rfmScore) - parseFloat(a.rfmScore));
    return { results, cutoffs };
}

// ── Custom RFM Engine ────────────────────────────────────────
function matchesRule(actual, rule) {
    if (!rule || rule.type === "any") return true;
    const v = parseFloat(rule.value);
    const v2 = parseFloat(rule.value2);
    if (isNaN(v)) return true;
    if (rule.type === "lt") return actual < v;
    if (rule.type === "gt") return actual > v;
    if (rule.type === "between") return !isNaN(v2) && actual >= v && actual <= v2;
    if (rule.type === "eq")  return actual === v;
    if (rule.type === "lte") return actual <= v;
    if (rule.type === "gte") return actual >= v;
    return true;
}

function runCustomRFM(rows, customerCol, dateCol, amountCol, customSegments) {
    const customerMap = {};
    const today = new Date();
    rows.forEach(row => {
        const customerId = row[customerCol];
        const date = parseDate(row[dateCol]);
        const amount = parseFloat(row[amountCol]);
        if (!customerId || !date || isNaN(amount)) return;
        if (!customerMap[customerId]) customerMap[customerId] = { customerId, orders: [], totalSpend: 0 };
        customerMap[customerId].orders.push({ date, amount });
        customerMap[customerId].totalSpend += amount;
    });
    const customers = Object.values(customerMap);
    if (customers.length === 0) return [];
    return customers.map(c => {
        const lastOrder = new Date(Math.max(...c.orders.map(o => o.date)));
        const recencyDays = Math.floor((today - lastOrder) / (1000 * 60 * 60 * 24));
        const frequency = c.orders.length;
        const monetary = c.totalSpend;
        let matched = null;
        for (const seg of customSegments) {
            if (matchesRule(recencyDays, seg.recency) && matchesRule(frequency, seg.frequency) && matchesRule(monetary, seg.monetary)) {
                matched = seg; break;
            }
        }
        const segInfo = matched
            ? { segment: matched.name, label: matched.name + " " + (matched.emoji || ""), color: matched.color, bg: matched.color + "22", description: matched.description || "", action: matched.action || "" }
            : { segment: "Unclassified", label: "Unclassified", color: C.dim, bg: "#f3f4f6", description: "Didn't match any custom segment.", action: "Review your segment rules or adjust thresholds." };
        return {
            customerId: c.customerId, recencyDays, frequency, monetary,
            lastOrderDate: lastOrder.toISOString().split("T")[0],
            rScore: "—", fScore: "—", mScore: "—", rfmScore: "—",
            ...segInfo,
        };
    }).sort((a, b) => a.segment.localeCompare(b.segment));
}

// ── Rule Editor Component ─────────────────────────────────
function RuleEditor({ label, icon, rule, onChange }) {
    const r = rule || { type: "any" };
    const inputStyle = {
        background: "#ffffff",
        border: "1px solid " + C.border,
        borderRadius: 6,
        padding: "0.35rem 0.5rem",
        color: C.text,
        fontFamily: MONO,
        fontSize: "0.75rem",
        outline: "none",
    };
    return (
        <div style={{ marginBottom: "0.75rem" }}>
            <div style={{ fontSize: "0.7rem", color: C.dim, marginBottom: "0.35rem" }}>{icon} {label}</div>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                <select value={r.type || "any"} onChange={e => onChange({ ...r, type: e.target.value })}
                    style={{ ...inputStyle }}>
                    <option value="any">Any</option>
                    <option value="eq">Equals</option>
                    <option value="lt">Less than</option>
                    <option value="lte">Less than or equal</option>
                    <option value="gt">Greater than</option>
                    <option value="gte">Greater than or equal</option>
                    <option value="between">Between</option>
                </select>
                {r.type !== "any" && r.type && (
                    <input type="number" value={r.value || ""} onChange={e => onChange({ ...r, value: e.target.value })}
                        placeholder="Value" style={{ ...inputStyle, width: 90 }} />
                )}
                {r.type === "between" && (
                    <>
                        <span style={{ color: C.dim, fontSize: "0.75rem" }}>and</span>
                        <input type="number" value={r.value2 || ""} onChange={e => onChange({ ...r, value2: e.target.value })}
                            placeholder="Value" style={{ ...inputStyle, width: 90 }} />
                    </>
                )}
                {label === "Recency (days)" && r.type !== "any" && (
                    <span style={{ fontSize: "0.68rem", color: C.dim }}>days</span>
                )}
                {label === "Frequency (orders)" && r.type !== "any" && (
                    <span style={{ fontSize: "0.68rem", color: C.dim }}>orders</span>
                )}
            </div>
        </div>
    );
}

// ── Segment Builder ───────────────────────────────────────
const SEGMENT_COLORS = ["#059669", "#52658F", "#b45309", "#dc2626", "#2a6a8a", "#c2410c", "#7c3aed", "#be185d"];
const EMOJIS = ["🏆", "⭐", "📈", "⚠️", "💎", "🌱", "💤", "🔍", "🎯", "💡"];

function SegmentBuilder({ segments, onChange }) {
    function addSegment() {
        const newSeg = {
            id: Date.now(),
            name: "Segment " + (segments.length + 1),
            emoji: EMOJIS[segments.length % EMOJIS.length],
            color: SEGMENT_COLORS[segments.length % SEGMENT_COLORS.length],
            recency: { type: "any" },
            frequency: { type: "any" },
            monetary: { type: "any" },
            description: "",
            action: "",
        };
        onChange([...segments, newSeg]);
    }

    function updateSegment(id, updates) {
        onChange(segments.map(s => s.id === id ? { ...s, ...updates } : s));
    }

    function deleteSegment(id) {
        onChange(segments.filter(s => s.id !== id));
    }

    function moveUp(index) {
        if (index === 0) return;
        const arr = [...segments];
        [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
        onChange(arr);
    }

    function moveDown(index) {
        if (index === segments.length - 1) return;
        const arr = [...segments];
        [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
        onChange(arr);
    }

    return (
        <div>
            <div style={{ fontSize: "0.72rem", color: C.muted, marginBottom: "1rem", lineHeight: 1.6 }}>
                Customers are matched to the <strong style={{ color: C.text }}>first segment</strong> whose rules they satisfy — order matters.
                Unmatched customers go to "Unclassified".
            </div>

            {segments.map((seg, index) => (
                <div key={seg.id} style={{ background: "#ffffff", border: "1.5px solid " + seg.color + "55", borderRadius: 12, padding: "1.25rem", marginBottom: "0.75rem" }}>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap" }}>
                        <select value={seg.emoji} onChange={e => updateSegment(seg.id, { emoji: e.target.value })}
                            style={{ background: "#ffffff", border: "1px solid " + C.border, borderRadius: 6, padding: "0.3rem 0.4rem", color: C.text, fontFamily: MONO, fontSize: "0.85rem", outline: "none" }}>
                            {EMOJIS.map(e => <option key={e} value={e}>{e}</option>)}
                        </select>
                        <input value={seg.name} onChange={e => updateSegment(seg.id, { name: e.target.value })}
                            style={{ background: "#ffffff", border: "1px solid " + C.border, borderRadius: 6, padding: "0.35rem 0.75rem", color: C.text, fontFamily: SYNE, fontWeight: 700, fontSize: "0.88rem", outline: "none", flex: 1, minWidth: 120 }} />
                        <div style={{ display: "flex", gap: "0.25rem" }}>
                            {SEGMENT_COLORS.map(col => (
                                <div key={col} onClick={() => updateSegment(seg.id, { color: col })}
                                    style={{ width: 18, height: 18, borderRadius: "50%", background: col, cursor: "pointer", border: seg.color === col ? "2px solid " + C.text : "2px solid transparent" }} />
                            ))}
                        </div>
                        <div style={{ display: "flex", gap: "0.25rem", marginLeft: "auto" }}>
                            <button onClick={() => moveUp(index)} disabled={index === 0}
                                style={{ background: "transparent", border: "1px solid " + C.border, borderRadius: 4, padding: "0.2rem 0.4rem", color: index === 0 ? C.grey : C.muted, cursor: index === 0 ? "not-allowed" : "pointer", fontSize: "0.7rem" }}>↑</button>
                            <button onClick={() => moveDown(index)} disabled={index === segments.length - 1}
                                style={{ background: "transparent", border: "1px solid " + C.border, borderRadius: 4, padding: "0.2rem 0.4rem", color: index === segments.length - 1 ? C.grey : C.muted, cursor: index === segments.length - 1 ? "not-allowed" : "pointer", fontSize: "0.7rem" }}>↓</button>
                            <button onClick={() => deleteSegment(seg.id)}
                                style={{ background: "transparent", border: "1px solid #fca5a5", borderRadius: 4, padding: "0.2rem 0.5rem", color: "#dc2626", cursor: "pointer", fontSize: "0.7rem" }}>✕</button>
                        </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
                        <RuleEditor label="Recency (days)" icon="📅" rule={seg.recency} onChange={v => updateSegment(seg.id, { recency: v })} />
                        <RuleEditor label="Frequency (orders)" icon="🔢" rule={seg.frequency} onChange={v => updateSegment(seg.id, { frequency: v })} />
                        <RuleEditor label="Monetary (value)" icon="💰" rule={seg.monetary} onChange={v => updateSegment(seg.id, { monetary: v })} />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                        <div>
                            <div style={{ fontSize: "0.68rem", color: C.dim, marginBottom: "0.25rem" }}>Description (for PDF)</div>
                            <input value={seg.description} onChange={e => updateSegment(seg.id, { description: e.target.value })}
                                placeholder="e.g. Your best customers"
                                style={{ background: "#ffffff", border: "1px solid " + C.border, borderRadius: 6, padding: "0.35rem 0.6rem", color: C.text, fontFamily: MONO, fontSize: "0.75rem", width: "100%", outline: "none", boxSizing: "border-box" }} />
                        </div>
                        <div>
                            <div style={{ fontSize: "0.68rem", color: C.dim, marginBottom: "0.25rem" }}>Recommended action</div>
                            <input value={seg.action} onChange={e => updateSegment(seg.id, { action: e.target.value })}
                                placeholder="e.g. Send loyalty reward"
                                style={{ background: "#ffffff", border: "1px solid " + C.border, borderRadius: 6, padding: "0.35rem 0.6rem", color: C.text, fontFamily: MONO, fontSize: "0.75rem", width: "100%", outline: "none", boxSizing: "border-box" }} />
                        </div>
                    </div>
                </div>
            ))}

            <button onClick={addSegment}
                style={{ width: "100%", background: "transparent", border: "1.5px dashed " + C.border, borderRadius: 10, padding: "0.75rem", color: C.accent, fontFamily: SYNE, fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                + Add Segment
            </button>
        </div>
    );
}

// ── Rule Description Helper ───────────────────────────────
function ruleDesc(rule, unit = "") {
    if (!rule || rule.type === "any") return "Any";
    const v = rule.value;
    const v2 = rule.value2;
    if (rule.type === "lt") return `< ${Number(v).toLocaleString()} ${unit}`.trim();
    if (rule.type === "gt") return `> ${Number(v).toLocaleString()} ${unit}`.trim();
    if (rule.type === "between") return `${Number(v).toLocaleString()} – ${Number(v2).toLocaleString()} ${unit}`.trim();
    return "Any";
}

// ── Sample Data ───────────────────────────────────────────
const SAMPLE_RFM_DATA = [
    { order_id: "ORD-11001", customer_id: "CUST-0001", order_date: "2026-05-28", order_total: "125000" },
    { order_id: "ORD-11002", customer_id: "CUST-0001", order_date: "2026-06-02", order_total: "98000" },
    { order_id: "ORD-11003", customer_id: "CUST-0001", order_date: "2026-06-05", order_total: "145000" },
    { order_id: "ORD-11004", customer_id: "CUST-0002", order_date: "2026-05-15", order_total: "75000" },
    { order_id: "ORD-11005", customer_id: "CUST-0002", order_date: "2026-05-22", order_total: "82000" },
    { order_id: "ORD-11006", customer_id: "CUST-0002", order_date: "2026-05-29", order_total: "91000" },
    { order_id: "ORD-11007", customer_id: "CUST-0002", order_date: "2026-06-04", order_total: "78000" },
    { order_id: "ORD-11008", customer_id: "CUST-0003", order_date: "2026-06-06", order_total: "55000" },
    { order_id: "ORD-11009", customer_id: "CUST-0004", order_date: "2025-12-10", order_total: "210000" },
    { order_id: "ORD-11010", customer_id: "CUST-0004", order_date: "2025-12-28", order_total: "185000" },
    { order_id: "ORD-11011", customer_id: "CUST-0004", order_date: "2026-01-15", order_total: "220000" },
    { order_id: "ORD-11012", customer_id: "CUST-0005", order_date: "2026-04-01", order_total: "65000" },
    { order_id: "ORD-11013", customer_id: "CUST-0005", order_date: "2026-04-14", order_total: "72000" },
    { order_id: "ORD-11014", customer_id: "CUST-0005", order_date: "2026-04-28", order_total: "68000" },
    { order_id: "ORD-11015", customer_id: "CUST-0006", order_date: "2026-06-01", order_total: "95000" },
    { order_id: "ORD-11016", customer_id: "CUST-0007", order_date: "2025-10-05", order_total: "45000" },
    { order_id: "ORD-11017", customer_id: "CUST-0008", order_date: "2026-05-20", order_total: "88000" },
    { order_id: "ORD-11018", customer_id: "CUST-0008", order_date: "2026-05-27", order_total: "92000" },
    { order_id: "ORD-11019", customer_id: "CUST-0008", order_date: "2026-06-03", order_total: "85000" },
    { order_id: "ORD-11020", customer_id: "CUST-0009", order_date: "2026-03-15", order_total: "155000" },
    { order_id: "ORD-11021", customer_id: "CUST-0009", order_date: "2026-03-28", order_total: "168000" },
    { order_id: "ORD-11022", customer_id: "CUST-0010", order_date: "2026-06-05", order_total: "62000" },
    { order_id: "ORD-11023", customer_id: "CUST-0011", order_date: "2026-01-10", order_total: "320000" },
    { order_id: "ORD-11024", customer_id: "CUST-0011", order_date: "2026-02-05", order_total: "295000" },
    { order_id: "ORD-11025", customer_id: "CUST-0012", order_date: "2026-05-10", order_total: "71000" },
    { order_id: "ORD-11026", customer_id: "CUST-0012", order_date: "2026-05-24", order_total: "79000" },
    { order_id: "ORD-11027", customer_id: "CUST-0012", order_date: "2026-06-04", order_total: "83000" },
    { order_id: "ORD-11028", customer_id: "CUST-0013", order_date: "2025-11-20", order_total: "52000" },
    { order_id: "ORD-11029", customer_id: "CUST-0014", order_date: "2026-04-10", order_total: "110000" },
    { order_id: "ORD-11030", customer_id: "CUST-0014", order_date: "2026-04-25", order_total: "125000" },
];

// ── Main Page ─────────────────────────────────────────────
export default function RFMPage() {
    const navigate = useNavigate();
    const fileRef = useRef();

    const [step, setStep] = useState("upload");
    const [fileName, setFileName] = useState("");
    const [data, setData] = useState(null);
    const [config, setConfig] = useState({ customerCol: "", dateCol: "", amountCol: "" });
    const [mode, setMode] = useState("default");
    const [customSegments, setCustomSegments] = useState([]);
    const [results, setResults] = useState(null);
    const [cutoffs, setCutoffs] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    useEffect(() => {
        if (!document.querySelector('link[href*="Fraunces"]')) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = FONT_LINK;
            document.head.appendChild(link);
        }
    }, []);

    async function handleFile(e) {
        const file = e.target.files[0];
        if (!file) return;
        setFileName(file.name);
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, dateNF: "yyyy-mm-dd", defval: "" });
        const headers = raw[0].map(h => String(h || "").trim()).filter(h => h !== "");
        const rows = raw.slice(1).filter(row => row.some(cell => cell !== "" && cell !== null)).map(row => {
            const obj = {}; headers.forEach((h, i) => { obj[h] = String(row[i] || "").trim(); }); return obj;
        });
        setData({ headers, rows });
        const dateCol = headers.find(h => h.toLowerCase().includes("date") || h.toLowerCase().includes("tanggal")) || "";
        const amountCol = headers.find(h => h.toLowerCase().includes("total") || h.toLowerCase().includes("amount") || h.toLowerCase().includes("sales") || h.toLowerCase().includes("harga")) || "";
        const customerCol = headers.find(h => h.toLowerCase().includes("customer") || h.toLowerCase().includes("pelanggan") || h.toLowerCase().includes("phone") || h.toLowerCase().includes("user") || h.toLowerCase().includes("member")) || "";
        setConfig({ customerCol, dateCol, amountCol });
        setStep("configure");
    }

    function handleAnalyze() {
        setLoading(true);
        if (mode === "default") {
            const { results: r, cutoffs: c } = runRFM(data.rows, config.customerCol, config.dateCol, config.amountCol);
            setResults(r);
            setCutoffs(c);
        } else {
            const r = runCustomRFM(data.rows, config.customerCol, config.dateCol, config.amountCol, customSegments);
            setResults(r);
            setCutoffs(null);
        }
        setLoading(false);
        setStep("results");
    }

    const isConfigComplete = config.customerCol && config.dateCol && config.amountCol;

    const segmentCounts = {};
    if (results) results.forEach(r => { segmentCounts[r.segment] = (segmentCounts[r.segment] || 0) + 1; });
    const allSegmentNames = results ? [...new Set(results.map(r => r.segment))] : [];
    const topSegment = allSegmentNames.length > 0 ? allSegmentNames.reduce((a, b) => (segmentCounts[a] || 0) >= (segmentCounts[b] || 0) ? a : b) : "—";
    const avgRFM = results && results.some(r => r.rfmScore !== "—")
        ? (results.filter(r => r.rfmScore !== "—").reduce((s, r) => s + parseFloat(r.rfmScore), 0) / results.filter(r => r.rfmScore !== "—").length).toFixed(1)
        : "—";

    function downloadSegmentCSV(segmentName) {
        const rows = segmentName === "all"
            ? results
            : results.filter(r => r.segment === segmentName);

        const headers = ["customer_id", "last_order_date", "recency_days", "frequency", "monetary", "rfm_score", "segment"];
        const csvRows = [
            headers.join(","),
            ...rows.map(r => [
                r.customerId,
                r.lastOrderDate,
                r.recencyDays,
                r.frequency,
                Math.round(r.monetary),
                r.rfmScore,
                r.segment,
            ].join(","))
        ];

        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const safeName = segmentName === "all" ? "all-customers" : segmentName.toLowerCase().replace(/\s+/g, "-");
        a.href = url;
        a.download = `dgb-${safeName}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }
    
    async function exportPDF() {
        const { jsPDF } = await import("jspdf");
        const autoTable = (await import("jspdf-autotable")).default;
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        let logoDataUrl = null;
        try {
            const res = await fetch("https://datagetsbalanced.com/dgb-logo-dark.png");
            const blob = await res.blob();
            logoDataUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            });
        } catch (e) { console.warn("Logo fetch failed:", e); }

        doc.setFillColor(10, 10, 15);
        doc.rect(0, 0, pageWidth, 28, "F");
        if (logoDataUrl) {
            try { doc.addImage(logoDataUrl, "PNG", 10, 3, 32, 16); } catch (e) { console.warn("Logo draw failed:", e); }
        }
        doc.setTextColor(82, 101, 143);
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.text("DATA GETS BALANCED", pageWidth - 14, 11, { align: "right" });
        doc.setTextColor(136, 136, 136);
        doc.setFont("helvetica", "normal");
        doc.text("datagetsbalanced.com", pageWidth - 14, 18, { align: "right" });

        doc.setFillColor(22, 22, 42);
        doc.rect(0, 28, pageWidth, 12, "F");
        doc.setTextColor(232, 230, 225);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Customer Segmentation Report", 14, 36);
        doc.setTextColor(136, 136, 136);
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.text(`File: ${fileName}   |   Mode: ${mode === "default" ? "Quartile (Default)" : "Custom Segments"}   |   Generated: ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`, pageWidth - 14, 36, { align: "right" });

        doc.setFillColor(30, 30, 46);
        doc.rect(0, 40, pageWidth, 20, "F");
        const metrics = [
            { label: "Total Customers", value: String(results.length) },
            { label: "Avg RFM Score", value: avgRFM !== "—" ? avgRFM + " / 5.0" : "—" },
            { label: "At Risk", value: String((segmentCounts["At Risk"] || 0) + (segmentCounts["Can't Lose"] || 0)) },
            { label: "Top Segment", value: topSegment },
        ];
        metrics.forEach((m, i) => {
            const x = 14 + i * 48;
            doc.setFontSize(6.5); doc.setFont("helvetica", "normal"); doc.setTextColor(136, 136, 136);
            doc.text(m.label.toUpperCase(), x, 47);
            doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(232, 230, 225);
            doc.text(m.value, x, 55);
        });

        let y = 68;
        const stripEmoji = (str) => str ? str.replace(/[\u{1F300}-\u{1FFFF}]|[\u{2600}-\u{27BF}]/gu, "").trim() : "";

        doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 46);
        doc.text("How customers were segmented", 14, y);
        y += 4;

        if (mode === "default" && cutoffs) {
            doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(100, 100, 120);
            doc.text("Segments assigned using quartile scoring (1-5) based on data distribution.", 14, y + 5);
            y += 10;
            const cutoffRows = [
                ["5 - Best", `< ${Math.round(cutoffs.recency.p20)}d`, `>= ${Math.round(cutoffs.frequency.p80)}x`, `>= ${Math.round(cutoffs.monetary.p80).toLocaleString()}`],
                ["4", `${Math.round(cutoffs.recency.p20)}-${Math.round(cutoffs.recency.p40)}d`, `${Math.round(cutoffs.frequency.p60)}-${Math.round(cutoffs.frequency.p80)}x`, `${Math.round(cutoffs.monetary.p60).toLocaleString()}-${Math.round(cutoffs.monetary.p80).toLocaleString()}`],
                ["3", `${Math.round(cutoffs.recency.p40)}-${Math.round(cutoffs.recency.p60)}d`, `${Math.round(cutoffs.frequency.p40)}-${Math.round(cutoffs.frequency.p60)}x`, `${Math.round(cutoffs.monetary.p40).toLocaleString()}-${Math.round(cutoffs.monetary.p60).toLocaleString()}`],
                ["2", `${Math.round(cutoffs.recency.p60)}-${Math.round(cutoffs.recency.p80)}d`, `${Math.round(cutoffs.frequency.p20)}-${Math.round(cutoffs.frequency.p40)}x`, `${Math.round(cutoffs.monetary.p20).toLocaleString()}-${Math.round(cutoffs.monetary.p40).toLocaleString()}`],
                ["1 - Worst", `> ${Math.round(cutoffs.recency.p80)}d`, `< ${Math.round(cutoffs.frequency.p20)}x`, `< ${Math.round(cutoffs.monetary.p20).toLocaleString()}`],
            ];
            autoTable(doc, {
                startY: y + 2, head: [["Score", "Recency", "Frequency", "Monetary"]], body: cutoffRows,
                headStyles: { fillColor: [30, 30, 46], textColor: [232, 230, 225], fontStyle: "bold", fontSize: 8 },
                bodyStyles: { fontSize: 7.5, textColor: [50, 50, 70] },
                alternateRowStyles: { fillColor: [248, 248, 255] },
                margin: { left: 14, right: 14 },
            });
            y = doc.lastAutoTable.finalY + 8;

            doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 46);
            doc.text("Segment descriptions", 14, y);
            const legendRows = [
                ["Champions", "R>=4, F>=4", "Best customers. Buy often, recently, spend the most.", "Reward them. Ask for reviews."],
                ["Loyal", "R>=3, F>=3, M>=3", "Consistent customers who order regularly.", "Upsell premium. Loyalty program."],
                ["New", "R>=4, F<=2", "Ordered recently, haven't returned much yet.", "Welcome promo. Encourage 2nd order."],
                ["Promising", "R>=3, F>=2, M>=2", "Showing good signs - growing frequency.", "Targeted offers. Show best sellers."],
                ["At Risk", "R<=2, F>=3", "Used to order frequently, haven't recently.", "Win-back campaign. Special discount."],
                ["Can't Lose", "R<=2, F<=2, M>=3", "High spenders gone quiet. Critical.", "Personal outreach. Act fast."],
                ["Lost", "R<=1, F<=1", "Haven't ordered in a long time.", "Re-engagement if budget allows."],
            ];
            autoTable(doc, {
                startY: y + 4, head: [["Segment", "Score Rule", "Description", "Action"]],
                body: legendRows,
                headStyles: { fillColor: [30, 30, 46], textColor: [232, 230, 225], fontStyle: "bold", fontSize: 8 },
                bodyStyles: { fontSize: 7, textColor: [50, 50, 70] },
                columnStyles: { 0: { fontStyle: "bold", cellWidth: 28 }, 1: { cellWidth: 22 }, 3: { cellWidth: 50 } },
                alternateRowStyles: { fillColor: [248, 248, 255] },
                margin: { left: 14, right: 14 },
            });
            y = doc.lastAutoTable.finalY + 8;
        } else {
            doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(100, 100, 120);
            doc.text("Custom segments - first matching rule wins.", 14, y + 5);
            y += 10;
            const customRows = customSegments.map((seg, i) => [
                String(i + 1), stripEmoji(seg.name),
                ruleDesc(seg.recency, "days"), ruleDesc(seg.frequency, "orders"), ruleDesc(seg.monetary),
                seg.description || "—",
            ]);
            autoTable(doc, {
                startY: y + 2,
                head: [["#", "Segment", "Recency", "Frequency", "Monetary", "Description"]],
                body: customRows,
                headStyles: { fillColor: [30, 30, 46], textColor: [232, 230, 225], fontStyle: "bold", fontSize: 8 },
                bodyStyles: { fontSize: 7.5, textColor: [50, 50, 70] },
                columnStyles: { 0: { cellWidth: 8 }, 1: { fontStyle: "bold", cellWidth: 30 } },
                alternateRowStyles: { fillColor: [248, 248, 255] },
                margin: { left: 14, right: 14 },
            });
            y = doc.lastAutoTable.finalY + 8;
        }

        doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 46);
        doc.text("Segment Breakdown", 14, y);
        const breakdownRows = allSegmentNames.map(s => {
            const count = segmentCounts[s] || 0;
            const pct = ((count / results.length) * 100).toFixed(1) + "%";
            const inSeg = results.filter(r => r.segment === s);
            const avgSpend = inSeg.length > 0 ? (inSeg.reduce((sum, r) => sum + r.monetary, 0) / inSeg.length).toFixed(0) : "—";
            const avgFreq = inSeg.length > 0 ? (inSeg.reduce((sum, r) => sum + r.frequency, 0) / inSeg.length).toFixed(1) : "—";
            const action = stripEmoji(inSeg[0]?.action || "—");
            return [stripEmoji(s), String(count), pct, Number(avgSpend).toLocaleString(), avgFreq + "x", action];
        });
        autoTable(doc, {
            startY: y + 4,
            head: [["Segment", "Count", "%", "Avg Spend", "Avg Orders", "Recommended Action"]],
            body: breakdownRows,
            headStyles: { fillColor: [30, 30, 46], textColor: [232, 230, 225], fontStyle: "bold", fontSize: 8 },
            bodyStyles: { fontSize: 7.5, textColor: [50, 50, 70] },
            columnStyles: { 0: { fontStyle: "bold" }, 5: { cellWidth: 55, fontSize: 7 } },
            alternateRowStyles: { fillColor: [248, 248, 255] },
            margin: { left: 14, right: 14 },
        });

        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 46);
        doc.text("Full Customer List", 14, finalY);
        const customerRows = results.map(r => [
            r.customerId, r.recencyDays + "d", r.frequency + "x",
            Number(r.monetary).toLocaleString(),
            r.rfmScore !== "—" ? r.rScore + "/" + r.fScore + "/" + r.mScore : "—",
            r.rfmScore, stripEmoji(r.segment),
        ]);
        autoTable(doc, {
            startY: finalY + 4,
            head: [["Customer", "Recency", "Freq", "Spend", "R/F/M", "Score", "Segment"]],
            body: customerRows,
            headStyles: { fillColor: [30, 30, 46], textColor: [232, 230, 225], fontStyle: "bold", fontSize: 8 },
            bodyStyles: { fontSize: 7.5, textColor: [50, 50, 70] },
            alternateRowStyles: { fillColor: [248, 248, 255] },
            margin: { left: 14, right: 14 },
        });

        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.setFontSize(6.5); doc.setFont("helvetica", "normal"); doc.setTextColor(136, 136, 136);
            doc.text(`DGB — Data Gets Balanced · datagetsbalanced.com · Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: "center" });
        }

        doc.save(`dgb-segmentation-${fileName.replace(/\.[^.]+$/, "") || "report"}.pdf`);
    }

    function loadSampleRFM() {
        const { results: r, cutoffs: c } = runRFM(SAMPLE_RFM_DATA, "customer_id", "order_date", "order_total");
        setFileName("sample_restaurant_data.xlsx");
        setData({ headers: Object.keys(SAMPLE_RFM_DATA[0]), rows: SAMPLE_RFM_DATA });
        setConfig({ customerCol: "customer_id", dateCol: "order_date", amountCol: "order_total" });
        setResults(r);
        setCutoffs(c);
        setMode("default");
        setStep("results");
    }

    return (
        <div style={{ background: C.bg, minHeight: "100vh", color: C.text }}>

            {/* Topbar — black navbar matching Landing */}
            <div style={{
                borderBottom: "1px solid #222",
                padding: "0.75rem 1.5rem",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                position: "sticky", top: 0, background: "#000000", zIndex: 10,
            }}>
                <img src={require("../assets/dgb-logo-dark.png")} alt="DGB"
                    style={{ height: 80, cursor: "pointer", marginLeft: "-16px", marginTop: "-4px", marginBottom: "-4px" }}
                    onClick={() => navigate("/")} />
                <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                    {step !== "upload" && (
                        <button onClick={() => { setStep("upload"); setResults(null); setCustomSegments([]); }}
                            style={{ background: "transparent", border: "1px solid #333", borderRadius: 6, padding: "0.35rem 1rem", color: "#888", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>
                            ← New Analysis
                        </button>
                    )}
                    <button onClick={() => navigate("/app")}
                        style={{ background: "transparent", border: "none", color: "#888", fontFamily: MONO, fontSize: "0.85rem", cursor: "pointer" }}>
                        Reconciliation
                    </button>
                    <button onClick={() => navigate("/outlier")}
                        style={{ background: "transparent", border: "none", color: "#888", fontFamily: MONO, fontSize: "0.85rem", cursor: "pointer" }}>
                        Outlier Detection
                    </button>
                    <button onClick={async () => { await supabase.auth.signOut(); navigate("/"); }}
                        style={{ background: C.accent, color: "white", border: "none", padding: "0.55rem 1.25rem", borderRadius: 8, fontFamily: SYNE, fontWeight: 600, cursor: "pointer", fontSize: "0.85rem" }}>
                        Log out
                    </button>
                </div>
            </div>

            {/* Upload Step */}
            {step === "upload" && (
                <div style={{ maxWidth: 600, margin: "0 auto", padding: "4rem 1.5rem", textAlign: "center", fontFamily: MONO }}>
                    <h1 style={{ fontFamily: SYNE, fontSize: "2rem", fontWeight: 800, color: C.text, marginBottom: "0.75rem" }}>Customer Segmentation</h1>
                    <p style={{ color: C.muted, marginBottom: "0.5rem" }}>Upload your transaction data — DGB segments your customers automatically.</p>
                    <p style={{ color: C.dim, fontSize: "0.8rem", marginBottom: "3rem" }}>Works with POS exports or any transaction file with customer IDs.</p>
                    <div onClick={() => fileRef.current && fileRef.current.click()}
                        style={{ border: "1.5px dashed " + C.border, borderRadius: 14, padding: "3rem", cursor: "pointer", background: C.surface, transition: "border-color 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = C.accent}
                        onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                        <input type="file" accept=".csv,.xlsx,.xls" ref={fileRef} style={{ display: "none" }} onChange={handleFile} />
                        <svg width={40} height={40} viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: "0.75rem" }}>
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        <div style={{ color: C.dim, fontSize: "0.85rem" }}>{fileName || "Click to upload CSV or Excel"}</div>
                    </div>
                    {data && (
                        <button onClick={() => setStep("configure")}
                            style={{ background: C.accent, color: "white", border: "none", borderRadius: 8, padding: "0.6rem 1.5rem", fontFamily: SYNE, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer", marginTop: "1rem" }}>
                            Continue with {fileName} →
                        </button>
                    )}
                    <button onClick={loadSampleRFM}
                        style={{ background: "transparent", border: "none", color: C.accent, fontFamily: MONO, fontSize: "0.78rem", cursor: "pointer", marginTop: "1rem", textDecoration: "underline" }}>
                        or load sample data →
                    </button>
                </div>
            )}

            {/* Configure Step */}
            {step === "configure" && data && (
                <div style={{ maxWidth: 760, margin: "0 auto", padding: "3rem 1.5rem", fontFamily: MONO }}>
                    <h1 style={{ fontFamily: SYNE, fontSize: "1.8rem", fontWeight: 800, color: C.text, marginBottom: "0.5rem" }}>Set up your analysis</h1>
                    <p style={{ color: C.muted, marginBottom: "2rem" }}>{data.rows.length} rows loaded from {fileName}.</p>

                    {/* Column mapping */}
                    <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: "1.5rem", marginBottom: "1.5rem" }}>
                        <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "1rem" }}>STEP 1 — MAP YOUR COLUMNS</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                            {[
                                { key: "customerCol", label: "Customer ID / Phone", icon: "👤", hint: "Unique identifier per customer" },
                                { key: "dateCol", label: "Order Date", icon: "📅", hint: "When the order was placed" },
                                { key: "amountCol", label: "Order Amount", icon: "💰", hint: "Total value of the order" },
                            ].map(({ key, label, icon, hint }) => (
                                <div key={key}>
                                    <div style={{ fontSize: "0.75rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, marginBottom: "0.25rem" }}>{icon} {label}</div>
                                    <div style={{ fontSize: "0.7rem", color: C.dim, marginBottom: "0.4rem" }}>{hint}</div>
                                    <select value={config[key]} onChange={e => setConfig({ ...config, [key]: e.target.value })}
                                        style={{ background: "#ffffff", border: "1.5px solid " + (config[key] ? C.accent : C.border), borderRadius: 8, padding: "0.6rem 0.75rem", color: C.text, fontFamily: MONO, fontSize: "0.85rem", width: "100%", outline: "none", cursor: "pointer" }}>
                                        <option value="">Select column...</option>
                                        {data.headers.map(h => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mode selector */}
                    <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: "1.5rem", marginBottom: "1.5rem" }}>
                        <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "1rem" }}>STEP 2 — SEGMENTATION METHOD</div>
                        <div style={{ display: "flex", gap: "0.75rem", marginBottom: mode === "custom" ? "1.5rem" : 0 }}>
                            {[
                                { id: "default", label: "Quartile (Default)", desc: "DGB automatically scores customers based on your data distribution. No setup needed." },
                                { id: "custom", label: "Custom Segments", desc: "Define your own segments with specific rules for recency, frequency, and spend." },
                            ].map(opt => (
                                <div key={opt.id} onClick={() => setMode(opt.id)}
                                    style={{ flex: 1, background: mode === opt.id ? "#e8ecf4" : "#ffffff", border: "1.5px solid " + (mode === opt.id ? C.accent : C.border), borderRadius: 10, padding: "1rem", cursor: "pointer", transition: "border-color 0.2s" }}>
                                    <div style={{ fontFamily: SYNE, fontWeight: 700, fontSize: "0.85rem", color: mode === opt.id ? C.accent : C.text, marginBottom: "0.35rem" }}>{opt.label}</div>
                                    <div style={{ fontSize: "0.75rem", color: C.dim, lineHeight: 1.5 }}>{opt.desc}</div>
                                </div>
                            ))}
                        </div>
                        {mode === "custom" && (
                            <SegmentBuilder segments={customSegments} onChange={setCustomSegments} />
                        )}
                    </div>

                    <button disabled={!isConfigComplete || loading || (mode === "custom" && customSegments.length === 0)}
                        onClick={handleAnalyze}
                        style={{ width: "100%", background: isConfigComplete && (mode === "default" || customSegments.length > 0) ? C.accent : C.border, color: isConfigComplete && (mode === "default" || customSegments.length > 0) ? "white" : C.dim, border: "none", padding: "0.85rem", borderRadius: 8, fontFamily: SYNE, fontWeight: 700, fontSize: "1rem", cursor: "pointer" }}>
                        {loading ? "Segmenting customers..." : mode === "custom" && customSegments.length === 0 ? "Add at least one segment to continue" : "Segment Customers →"}
                    </button>
                </div>
            )}

            {/* Results Step */}
            {step === "results" && results && (
                <div style={{ maxWidth: 1100, margin: "0 auto", padding: "2rem 1.5rem", fontFamily: MONO }}>

                    {/* Mode badge + reconfigure */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                            <span style={{ fontSize: "0.7rem", padding: "0.2rem 0.6rem", borderRadius: 100, background: mode === "default" ? "#e8ecf4" : "#d1fae5", color: mode === "default" ? C.accent : C.green, border: "1px solid " + (mode === "default" ? C.accent : C.green), fontFamily: SYNE, fontWeight: 700 }}>
                                {mode === "default" ? "Quartile Mode" : "Custom Mode"}
                            </span>
                            <span style={{ fontSize: "0.75rem", color: C.dim }}>{fileName}</span>
                        </div>
                        <button onClick={() => setStep("configure")}
                            style={{ background: "transparent", border: "1px solid " + C.border, borderRadius: 6, padding: "0.3rem 0.75rem", color: C.muted, fontFamily: MONO, fontSize: "0.72rem", cursor: "pointer" }}>
                            ⚙ Change settings
                        </button>
                    </div>

                    {/* Summary cards */}
                    <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem", flexWrap: "wrap" }}>
                        {[
                            { label: "TOTAL CUSTOMERS", value: String(results.length), color: C.accent, sub: "unique customers" },
                            { label: "AVG RFM SCORE", value: avgRFM !== "—" ? avgRFM + " / 5.0" : "—", color: C.accent, sub: avgRFM !== "—" ? "out of 5.0" : "custom mode" },
                            { label: "AT RISK", value: String((segmentCounts["At Risk"] || 0) + (segmentCounts["Can't Lose"] || 0)), color: "#dc2626", sub: "need attention" },
                            { label: "TOP SEGMENT", value: topSegment, color: C.green, sub: `${segmentCounts[topSegment] || 0} customers` },
                        ].map((card, i) => (
                            <div key={i} style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: "1.25rem", flex: 1, minWidth: 160 }}>
                                <div style={{ fontSize: "0.65rem", color: C.dim, letterSpacing: "0.1em", marginBottom: "0.5rem", fontFamily: SYNE, fontWeight: 600 }}>{card.label}</div>
                                <div style={{ fontSize: "1.3rem", fontWeight: 600, color: card.color, fontFamily: SYNE }}>{card.value}</div>
                                <div style={{ fontSize: "0.7rem", color: C.dim, marginTop: "0.25rem" }}>{card.sub}</div>
                            </div>
                        ))}
                    </div>

                    {/* Quartile cutoffs table */}
                    {mode === "default" && cutoffs && (
                        <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: "1.25rem", marginBottom: "1rem" }}>
                            <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.75rem" }}>HOW WE SCORED YOUR CUSTOMERS (QUARTILE CUTOFFS)</div>
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.75rem", fontFamily: MONO }}>
                                    <thead>
                                        <tr style={{ borderBottom: "1px solid " + C.border }}>
                                            {["Score", "Recency", "Frequency", "Monetary"].map(h => (
                                                <th key={h} style={{ padding: "0.4rem 0.75rem", textAlign: "left", color: C.dim, fontWeight: 600, fontSize: "0.65rem", letterSpacing: "0.08em" }}>{h.toUpperCase()}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { score: "5 — Best", r: `< ${Math.round(cutoffs.recency.p20)} days`, f: `≥ ${Math.round(cutoffs.frequency.p80)}x`, m: `≥ ${Math.round(cutoffs.monetary.p80).toLocaleString()}` },
                                            { score: "4", r: `${Math.round(cutoffs.recency.p20)}–${Math.round(cutoffs.recency.p40)} days`, f: `${Math.round(cutoffs.frequency.p60)}–${Math.round(cutoffs.frequency.p80)}x`, m: `${Math.round(cutoffs.monetary.p60).toLocaleString()}–${Math.round(cutoffs.monetary.p80).toLocaleString()}` },
                                            { score: "3", r: `${Math.round(cutoffs.recency.p40)}–${Math.round(cutoffs.recency.p60)} days`, f: `${Math.round(cutoffs.frequency.p40)}–${Math.round(cutoffs.frequency.p60)}x`, m: `${Math.round(cutoffs.monetary.p40).toLocaleString()}–${Math.round(cutoffs.monetary.p60).toLocaleString()}` },
                                            { score: "2", r: `${Math.round(cutoffs.recency.p60)}–${Math.round(cutoffs.recency.p80)} days`, f: `${Math.round(cutoffs.frequency.p20)}–${Math.round(cutoffs.frequency.p40)}x`, m: `${Math.round(cutoffs.monetary.p20).toLocaleString()}–${Math.round(cutoffs.monetary.p40).toLocaleString()}` },
                                            { score: "1 — Worst", r: `> ${Math.round(cutoffs.recency.p80)} days`, f: `< ${Math.round(cutoffs.frequency.p20)}x`, m: `< ${Math.round(cutoffs.monetary.p20).toLocaleString()}` },
                                        ].map((row, i) => (
                                            <tr key={i} style={{ borderBottom: "1px solid " + C.border, background: i % 2 === 0 ? "#ffffff" : C.surface }}>
                                                <td style={{ padding: "0.4rem 0.75rem", color: C.accent, fontWeight: 600 }}>{row.score}</td>
                                                <td style={{ padding: "0.4rem 0.75rem", color: C.muted }}>{row.r}</td>
                                                <td style={{ padding: "0.4rem 0.75rem", color: C.muted }}>{row.f}</td>
                                                <td style={{ padding: "0.4rem 0.75rem", color: C.muted }}>{row.m}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Custom segment rules summary */}
                    {mode === "custom" && (
                        <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: "1.25rem", marginBottom: "1rem" }}>
                            <div style={{ fontSize: "0.7rem", color: C.green, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.75rem" }}>YOUR CUSTOM SEGMENT RULES</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                                {customSegments.map((seg, i) => (
                                    <div key={seg.id} style={{ display: "flex", gap: "1rem", alignItems: "center", padding: "0.5rem 0.75rem", borderRadius: 8, background: "#ffffff", border: "1px solid " + C.border, fontSize: "0.78rem", flexWrap: "wrap" }}>
                                        <span style={{ color: seg.color, fontFamily: SYNE, fontWeight: 700, minWidth: 120 }}>{seg.emoji} {seg.name}</span>
                                        <span style={{ color: C.dim }}>R: <span style={{ color: C.muted }}>{ruleDesc(seg.recency, "days")}</span></span>
                                        <span style={{ color: C.dim }}>F: <span style={{ color: C.muted }}>{ruleDesc(seg.frequency, "orders")}</span></span>
                                        <span style={{ color: C.dim }}>M: <span style={{ color: C.muted }}>{ruleDesc(seg.monetary)}</span></span>
                                        <span style={{ color: C.dim, fontSize: "0.68rem", marginLeft: "auto" }}>#{i + 1} priority</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Export + Filter */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                            <span style={{ fontSize: "0.7rem", color: C.dim, fontFamily: SYNE, fontWeight: 600 }}>FILTER</span>
                            {["all", ...allSegmentNames].map(seg => {
                                const count = seg === "all" ? results.length : segmentCounts[seg] || 0;
                                return (
                                    <button key={seg} onClick={() => setFilter(seg)}
                                        style={{ background: filter === seg ? C.accent : "transparent", border: "1.5px solid " + (filter === seg ? C.accent : C.border), borderRadius: 6, padding: "0.3rem 0.8rem", fontFamily: MONO, fontSize: "0.72rem", color: filter === seg ? "white" : C.dim, cursor: "pointer" }}>
                                        {seg === "all" ? `All (${count})` : `${seg} (${count})`}
                                    </button>
                                );
                            })}
                        </div>
                        <button onClick={exportPDF}
                            style={{ background: "transparent", border: "1.5px solid " + C.border, borderRadius: 6, padding: "0.4rem 1rem", fontFamily: MONO, fontSize: "0.75rem", color: C.muted, cursor: "pointer" }}>
                            ↓ Export PDF
                        </button>
                        <button onClick={() => downloadSegmentCSV(filter)}
                            style={{ background: C.accent, color: "white", border: "none", borderRadius: 6, padding: "0.4rem 1rem", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>
                            ↓ Download {filter === "all" ? "All" : filter} CSV
                        </button>
                    </div>

                    {/* Table + Detail Panel */}
                    <div style={{ display: "flex", gap: "1rem" }}>
                        <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, overflow: "hidden", flex: 1 }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 0.8fr 0.8fr 0.8fr 0.8fr 1.2fr", gap: "0.5rem", padding: "0.6rem 1.25rem", borderBottom: "1px solid " + C.border, background: "#ffffff" }}>
                                {["Customer", "Recency", "Frequency", "Monetary", "Score", "Segment"].map(h => (
                                    <div key={h} style={{ fontSize: "0.62rem", color: C.dim, letterSpacing: "0.08rem", fontFamily: SYNE, fontWeight: 600 }}>{h.toUpperCase()}</div>
                                ))}
                            </div>
                            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                                {results.filter(r => filter === "all" || r.segment === filter).map((r, i) => {
                                    const isSelected = selectedCustomer && selectedCustomer.customerId === r.customerId;
                                    return (
                                        <div key={i} onClick={() => setSelectedCustomer(r)}
                                            style={{ display: "grid", gridTemplateColumns: "1.5fr 0.8fr 0.8fr 0.8fr 0.8fr 1.2fr", gap: "0.5rem", padding: "0.7rem 1.25rem", borderBottom: "1px solid " + C.border, background: isSelected ? "#e8ecf4" : i % 2 === 0 ? "#ffffff" : C.surface, borderLeft: isSelected ? "2px solid " + C.accent : "2px solid transparent", cursor: "pointer", transition: "background 0.15s" }}>
                                            <div style={{ fontSize: "0.78rem", color: C.text, fontWeight: 500 }}>{r.customerId}</div>
                                            <div style={{ fontSize: "0.75rem", color: C.muted }}>{r.recencyDays}d ago</div>
                                            <div style={{ fontSize: "0.75rem", color: C.muted }}>{r.frequency}x</div>
                                            <div style={{ fontSize: "0.75rem", color: C.muted }}>{Number(r.monetary).toLocaleString()}</div>
                                            <div style={{ fontSize: "0.75rem", color: C.accent, fontWeight: 600 }}>{r.rfmScore}</div>
                                            <div><span style={{ fontSize: "0.62rem", padding: "0.2rem 0.45rem", borderRadius: 4, background: r.bg, color: r.color, border: "1px solid " + r.color + "55", fontFamily: SYNE, fontWeight: 600 }}>{r.label}</span></div>
                                        </div>
                                    );
                                })}
                                {results.filter(r => filter === "all" || r.segment === filter).length === 0 && (
                                    <div style={{ padding: "3rem", textAlign: "center", color: C.dim, fontSize: "0.85rem" }}>No customers in this segment.</div>
                                )}
                            </div>
                        </div>

                        {/* Detail Panel */}
                        {selectedCustomer && (
                            <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: "1.25rem", width: 300, display: "flex", flexDirection: "column", gap: "1rem", maxHeight: "60vh", overflowY: "auto" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                    <div>
                                        <div style={{ fontSize: "0.65rem", color: C.accent, letterSpacing: "0.12rem", fontFamily: SYNE, fontWeight: 700, marginBottom: "0.5rem" }}>CUSTOMER PROFILE</div>
                                        <div style={{ fontFamily: SYNE, fontWeight: 700, fontSize: "1rem", color: C.text }}>{selectedCustomer.customerId}</div>
                                        <div style={{ marginTop: "0.5rem" }}>
                                            <span style={{ fontSize: "0.7rem", padding: "0.25rem 0.6rem", borderRadius: 4, background: selectedCustomer.bg, color: selectedCustomer.color, border: "1px solid " + selectedCustomer.color + "55", fontFamily: SYNE, fontWeight: 700 }}>
                                                {selectedCustomer.label}
                                            </span>
                                        </div>
                                    </div>
                                    <button onClick={() => setSelectedCustomer(null)}
                                        style={{ background: "transparent", border: "1px solid " + C.border, borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, cursor: "pointer", fontSize: "0.9rem", flexShrink: 0 }}>✕</button>
                                </div>

                                <div style={{ background: "#ffffff", border: "1px solid " + C.border, borderRadius: 8, padding: "1rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                                    {[
                                        { label: "Last Order", val: selectedCustomer.lastOrderDate, color: C.muted },
                                        { label: "Days Ago", val: `${selectedCustomer.recencyDays} days`, color: selectedCustomer.recencyDays < 30 ? C.green : "#dc2626" },
                                        { label: "Total Orders", val: `${selectedCustomer.frequency}x`, color: C.accent },
                                        { label: "Total Spend", val: Number(selectedCustomer.monetary).toLocaleString(), color: C.cyan },
                                        ...(selectedCustomer.rfmScore !== "—" ? [
                                            { label: "R Score", val: `${selectedCustomer.rScore}/5`, color: C.muted },
                                            { label: "F Score", val: `${selectedCustomer.fScore}/5`, color: C.muted },
                                            { label: "M Score", val: `${selectedCustomer.mScore}/5`, color: C.muted },
                                            { label: "RFM Score", val: selectedCustomer.rfmScore, color: C.accent },
                                        ] : []),
                                    ].map((item, i) => (
                                        <div key={i}>
                                            <div style={{ fontSize: "0.65rem", color: C.dim, marginBottom: "0.2rem" }}>{item.label}</div>
                                            <div style={{ fontSize: "0.82rem", color: item.color, fontWeight: 500 }}>{item.val}</div>
                                        </div>
                                    ))}
                                </div>

                                {(selectedCustomer.description || selectedCustomer.action) && (
                                    <div style={{ background: "#ffffff", border: "1px solid " + C.border, borderRadius: 8, padding: "1rem" }}>
                                        {selectedCustomer.description && (
                                            <>
                                                <div style={{ fontSize: "0.65rem", color: C.accent, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.4rem" }}>ABOUT THIS SEGMENT</div>
                                                <div style={{ fontSize: "0.82rem", color: C.muted, lineHeight: 1.7, marginBottom: "0.75rem" }}>{selectedCustomer.description}</div>
                                            </>
                                        )}
                                        {selectedCustomer.action && (
                                            <>
                                                <div style={{ fontSize: "0.65rem", color: C.green, fontFamily: SYNE, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.4rem" }}>RECOMMENDED ACTION</div>
                                                <div style={{ fontSize: "0.82rem", color: C.green, lineHeight: 1.7 }}>{selectedCustomer.action}</div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
